import { useRef, useState, type ReactNode, type CSSProperties } from 'react';
import type { FlowNodeData, FlowOrientation } from './flow.types.js';

/** Where a port sits, in client pixels, for whoever opens a menu beside it. */
export interface PortAnchor {
  clientX: number;
  clientY: number;
}

export interface FlowNodeProps {
  node: FlowNodeData;
  /** When true, the node is the active selection. */
  selected?: boolean;
  /** Custom inner renderer. Default shows `node.type` + first data field. */
  children?: ReactNode;
  /**
   * Called while dragging with the movement since the PREVIOUS call, in
   * client pixels. The canvas divides by its zoom and adds it to the node.
   */
  onDrag?: (id: string, delta: { x: number; y: number }) => void;
  /** Called once when a drag finishes (not after a plain click). */
  onDragEnd?: (id: string) => void;
  onClick?: (id: string) => void;
  /** Inline style override. */
  style?: CSSProperties;
  /**
   * Makes the out-handles connection sources: pointer-down on one reports
   * `onConnectStart` instead of starting a node drag (see `FlowCanvas.onConnect`).
   */
  connectable?: boolean;
  /** `handleId` names the output port the drag began on (undefined for the default handle). */
  onConnectStart?: (id: string, e: React.PointerEvent<HTMLElement>, handleId?: string) => void;
  /** Highlight as the hovered drop target of a pending connection. */
  connectTarget?: boolean;
  /** Where the handles sit: right/left (horizontal) or bottom/top (vertical). */
  orientation?: FlowOrientation;
  /**
   * Draws a "+" beside each output port while the node is selected or
   * hovered. Clicking it reports the port, so the consumer can add the next
   * node right there and link it.
   */
  onAddFrom?: (id: string, handleId: string | undefined, anchor: PortAnchor) => void;
  /**
   * Output handles that already carry a link ('' for the single default
   * output). Those show neither their label nor a "+": the link has both.
   */
  linkedHandles?: ReadonlySet<string>;
  /** Text for the "+" button's accessible name. */
  addLabel?: string;
}

/**
 * Where output port `index` of `count` sits along the node's out edge, as a
 * fraction of that edge. Ports spread evenly; a single port sits centred.
 * Shared with `FlowEdge` so edges leave from exactly where the handle is drawn.
 */
export function outputFraction(index: number, count: number): number {
  return count <= 1 ? 0.5 : (index + 1) / (count + 1);
}

/** Pointer movement under this many pixels is a click, not a drag. */
const DRAG_SLOP_PX = 3;

/**
 * Single canvas node: positioned absolutely at `node.position` in
 * flow-space (parent applies the zoom/pan transform). Pointer-based drag
 * uses a lightweight implementation so the canvas doesn't depend on
 * dnd-kit for node movement: the dnd-kit primitives in this subpath are
 * for the **palette → canvas drop** flow instead.
 */
export function FlowNode({
  node, selected, children, onDrag, onDragEnd, onClick, style,
  connectable, onConnectStart, connectTarget, orientation = 'horizontal',
  onAddFrom, addLabel = 'Add a node here', linkedHandles
}: FlowNodeProps): ReactNode {
  const drag = useRef<{ lastX: number; lastY: number; startX: number; startY: number; moved: boolean } | null>(null);
  const [hovered, setHovered] = useState(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // Capture on the node itself, not on whatever child was pressed, so a
    // press on the summary text drags the node exactly like a press on the
    // title does.
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { lastX: e.clientX, lastY: e.clientY, startX: e.clientX, startY: e.clientY, moved: false };
    e.stopPropagation();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    e.stopPropagation();
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_SLOP_PX) return;
    d.moved = true;
    // Only the movement since the last event: reporting the whole distance
    // since the press made every node fly away from the cursor.
    const delta = { x: e.clientX - d.lastX, y: e.clientY - d.lastY };
    d.lastX = e.clientX;
    d.lastY = e.clientY;
    if (delta.x !== 0 || delta.y !== 0) onDrag?.(node.id, delta);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    e.stopPropagation();
    if (d.moved) onDragEnd?.(node.id);
    else onClick?.(node.id);
  };

  const handleDown = (handleId?: string) => connectable
    ? (e: React.PointerEvent<HTMLElement>) => {
        if (e.button !== 0) return;
        // Not a node drag, not a canvas pan: the canvas tracks the connection.
        e.stopPropagation();
        e.preventDefault();
        onConnectStart?.(node.id, e, handleId);
      }
    : undefined;

  const outputs = node.outputs ?? [];
  const vertical = orientation === 'vertical';
  const showAdd = Boolean(onAddFrom) && (selected || hovered);

  const addButton = (handleId: string | undefined, at: string | undefined) => (
    <button
      type="button"
      className="rr-flow-node__add"
      style={vertical
        ? { left: at ?? '50%', top: 'auto', bottom: -34, transform: 'translateX(-50%)' }
        : { top: at ?? '50%', right: -34, transform: 'translateY(-50%)' }}
      aria-label={addLabel}
      title={addLabel}
      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
      onClick={(e) => {
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        onAddFrom?.(node.id, handleId, { clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 });
      }}
    >
      +
    </button>
  );

  return (
    <div
      className={[
        'rr-flow-node',
        vertical && 'rr-flow-node--vertical',
        selected && 'rr-flow-node--selected',
        hovered && 'rr-flow-node--hover',
        node.isStart && 'rr-flow-node--start',
        node.isEnd && 'rr-flow-node--end',
        connectTarget && 'rr-flow-node--connect-target'
      ].filter(Boolean).join(' ')}
      data-node-id={node.id}
      data-node-type={node.type}
      style={{
        position: 'absolute',
        left: node.position.x,
        top: node.position.y,
        width: node.width ?? 200,
        height: node.height ?? 80,
        ...style
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { drag.current = null; }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {children ?? (
        <div className="rr-flow-node__default">
          <div className="rr-flow-node__type">{node.type}</div>
          {node.data ? <div className="rr-flow-node__summary">{summary(node.data)}</div> : null}
        </div>
      )}
      {/* Connector handles: visual hints unless the canvas is `connectable`. */}
      <span className="rr-flow-node__handle rr-flow-node__handle--in" aria-hidden />
      {outputs.length === 0 ? (
        <>
          <span
            className="rr-flow-node__handle rr-flow-node__handle--out"
            data-connectable={connectable || undefined}
            aria-hidden
            onPointerDown={handleDown(undefined)}
          />
          {showAdd && !node.isEnd && !linkedHandles?.has('') ? addButton(undefined, undefined) : null}
        </>
      ) : outputs.map((o, i) => {
        const at = `${outputFraction(i, outputs.length) * 100}%`;
        const linked = linkedHandles?.has(o.id) ?? false;
        return (
          <span key={o.id} className="rr-flow-node__port-slot" style={{ display: 'contents' }}>
            <span
              className="rr-flow-node__handle rr-flow-node__handle--out rr-flow-node__handle--port"
              data-connectable={connectable || undefined}
              data-handle-id={o.id}
              aria-hidden
              style={vertical ? { left: at, top: 'auto', bottom: -6, transform: 'translateX(-50%)' } : { top: at }}
              onPointerDown={handleDown(o.id)}
            >
              {o.label && !linked ? <span className="rr-flow-node__port-label">{o.label}</span> : null}
            </span>
            {showAdd && !linked ? addButton(o.id, at) : null}
          </span>
        );
      })}
    </div>
  );
}

function summary(data: Record<string, unknown>): string {
  const first = Object.entries(data).find(([, v]) => typeof v === 'string' || typeof v === 'number');
  return first ? `${first[0]}: ${first[1]}` : '';
}
